import type { Attachment } from '@medplum/fhirtypes'
import { ImageZoom } from '@seen/ui'
import { IconAlertTriangle, IconPhoto } from '@tabler/icons-react'
import { type JSX, useCallback, useRef, useState } from 'react'
import { AttachmentCardLayout } from '#src/components/attachments/AttachmentCardLayout.tsx'
import { RetryConversionButton } from '#src/components/attachments/RetryConversionButton.tsx'
import { Skeleton } from '#src/components/core/Skeleton.tsx'
import { useAttachmentConversionStatus } from '#src/hooks/useAttachmentConversionStatus.ts'

interface AttachmentImageDisplayProps {
  attachment: Attachment
  url: string
  isExpanded: boolean
  onExpandedChange: (expanded: boolean) => void
  onTitleChange?: (title: string) => void
  /** Parent DocumentReference id; required to surface the retry-conversion button. */
  documentReferenceId?: string
}

export function AttachmentImageDisplay({
  attachment,
  url,
  isExpanded,
  onExpandedChange,
  onTitleChange,
  documentReferenceId,
}: AttachmentImageDisplayProps): JSX.Element {
  const title = attachment.title || 'Image'
  const [hasError, setHasError] = useState(false)
  const { status } = useAttachmentConversionStatus(attachment)

  const prevUrlRef = useRef(url)
  if (prevUrlRef.current !== url) {
    prevUrlRef.current = url
    if (hasError) {
      setHasError(false)
    }
  }

  const handleError = useCallback(() => {
    setHasError(true)
  }, [])

  let preview: JSX.Element
  if (status === 'pending') {
    preview = (
      <div className="relative h-48 w-full">
        <Skeleton
          role="status"
          aria-label="File converting"
          className="h-full w-full"
        />
        <p className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
          Converting...
        </p>
      </div>
    )
  } else if (status === 'failed') {
    preview = (
      <div
        role="alert"
        className="flex flex-col items-center justify-center gap-2 py-8 text-sm"
      >
        <IconAlertTriangle className="h-6 w-6 text-amber-500" />
        <p className="text-muted-foreground">
          Preview unavailable — this file couldn&apos;t be converted
        </p>
        <RetryConversionButton
          attachment={attachment}
          documentReferenceId={documentReferenceId}
        />
      </div>
    )
  } else if (hasError) {
    preview = (
      <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
        Preview not available — download to view
      </div>
    )
  } else {
    preview = (
      <ImageZoom isZoomed={isExpanded} onZoomChange={onExpandedChange}>
        <img
          className="max-h-[500px] w-full object-contain"
          src={url}
          alt={title}
          onError={handleError}
        />
      </ImageZoom>
    )
  }

  return (
    <AttachmentCardLayout
      attachment={attachment}
      title={title}
      icon={<IconPhoto className="h-4 w-4" />}
      onTitleChange={onTitleChange}
      conversionStatus={status}
      preview={preview}
    />
  )
}
